
import React, { useState } from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Bell, Clock, MessageSquare, Newspaper, Star, Zap } from 'lucide-react';

interface EmailPreferencesProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const EmailPreferencesProps: React.FC<EmailPreferencesProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [emailFrequency, setEmailFrequency] = useState(formData.emailFrequency || 'daily');
  const [notifications, setNotifications] = useState({
    newArticles: true,
    comments: true,
    journalistUpdates: true,
    weeklyDigest: true,
    trending: true,
    recommendations: true,
    ...formData.notifications
  });
  
  const handleFrequencyChange = (value: string) => {
    setEmailFrequency(value);
    updateFormData({ emailFrequency: value });
  };
  
  const handleNotificationChange = (key: string, checked: boolean) => {
    const updatedNotifications = {
      ...notifications,
      [key]: checked
    };
    setNotifications(updatedNotifications);
    updateFormData({ notifications: updatedNotifications });
  };
  
  const handleNext = () => {
    updateFormData({ 
      emailFrequency, 
      notifications
    });
    onNext();
  };

  return (
    <OnboardingLayout
      title="Email & Notification Preferences"
      subtitle="Customize how and when you hear from us"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleNext}
      onPrevious={onPrevious}
    >
      <div className="max-w-3xl mx-auto">
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h3 className="text-lg font-medium mb-4">Email Digest Frequency</h3>
            <p className="text-muted-foreground mb-6">
              How often would you like to receive our curated digest of articles and updates?
            </p>
            
            <RadioGroup 
              value={emailFrequency} 
              onValueChange={handleFrequencyChange}
              className="grid grid-cols-1 md:grid-cols-4 gap-4"
            >
              <div className="col-span-1">
                <RadioGroupItem value="daily" id="daily" className="hidden" />
                <Label 
                  htmlFor="daily" 
                  className={`flex flex-col items-center justify-between p-4 border rounded-lg ${
                    emailFrequency === 'daily' ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'
                  } cursor-pointer h-full`}
                >
                  <Clock className="h-8 w-8 mb-2 text-muted-foreground" />
                  <div className="text-center">
                    <div className="font-medium mb-1">Daily</div>
                    <p className="text-xs text-muted-foreground">
                      Get updates every day
                    </p>
                  </div>
                </Label>
              </div>
              
              <div className="col-span-1">
                <RadioGroupItem value="weekly" id="weekly" className="hidden" />
                <Label 
                  htmlFor="weekly" 
                  className={`flex flex-col items-center justify-between p-4 border rounded-lg ${
                    emailFrequency === 'weekly' ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'
                  } cursor-pointer h-full`}
                >
                  <Calendar7 className="h-8 w-8 mb-2 text-muted-foreground" />
                  <div className="text-center">
                    <div className="font-medium mb-1">Weekly</div>
                    <p className="text-xs text-muted-foreground">
                      Receive one digest per week
                    </p>
                  </div>
                </Label>
              </div>
              
              <div className="col-span-1">
                <RadioGroupItem value="monthly" id="monthly" className="hidden" />
                <Label 
                  htmlFor="monthly" 
                  className={`flex flex-col items-center justify-between p-4 border rounded-lg ${
                    emailFrequency === 'monthly' ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'
                  } cursor-pointer h-full`}
                >
                  <Calendar30 className="h-8 w-8 mb-2 text-muted-foreground" />
                  <div className="text-center">
                    <div className="font-medium mb-1">Monthly</div>
                    <p className="text-xs text-muted-foreground">
                      Monthly roundup only
                    </p>
                  </div>
                </Label>
              </div>
              
              <div className="col-span-1">
                <RadioGroupItem value="none" id="none" className="hidden" />
                <Label 
                  htmlFor="none" 
                  className={`flex flex-col items-center justify-between p-4 border rounded-lg ${
                    emailFrequency === 'none' ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'
                  } cursor-pointer h-full`}
                >
                  <Bell className="h-8 w-8 mb-2 text-muted-foreground" />
                  <div className="text-center">
                    <div className="font-medium mb-1">None</div>
                    <p className="text-xs text-muted-foreground">
                      Essential emails only
                    </p>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-medium mb-4">Notification Preferences</h3>
            <p className="text-muted-foreground mb-6">
              Choose which updates you'd like to receive:
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center mr-3">
                    <Newspaper className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label className="font-medium">New Articles</Label>
                    <p className="text-sm text-muted-foreground">From journalists you follow</p>
                  </div>
                </div>
                <Switch 
                  checked={notifications.newArticles} 
                  onCheckedChange={(checked) => handleNotificationChange('newArticles', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center mr-3">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label className="font-medium">Comments & Replies</Label>
                    <p className="text-sm text-muted-foreground">When someone replies to your comments</p>
                  </div>
                </div>
                <Switch 
                  checked={notifications.comments} 
                  onCheckedChange={(checked) => handleNotificationChange('comments', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center mr-3">
                    <Star className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label className="font-medium">Journalist Updates</Label>
                    <p className="text-sm text-muted-foreground">Announcements from followed journalists</p>
                  </div>
                </div>
                <Switch 
                  checked={notifications.journalistUpdates} 
                  onCheckedChange={(checked) => handleNotificationChange('journalistUpdates', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center mr-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label className="font-medium">Weekly Digest</Label>
                    <p className="text-sm text-muted-foreground">Summary of top content and updates</p>
                  </div>
                </div>
                <Switch 
                  checked={notifications.weeklyDigest} 
                  onCheckedChange={(checked) => handleNotificationChange('weeklyDigest', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center mr-3">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label className="font-medium">Trending Topics</Label>
                    <p className="text-sm text-muted-foreground">Alerts about popular discussions</p>
                  </div>
                </div>
                <Switch 
                  checked={notifications.trending} 
                  onCheckedChange={(checked) => handleNotificationChange('trending', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center mr-3">
                    <ThumbsUp className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label className="font-medium">Personalized Recommendations</Label>
                    <p className="text-sm text-muted-foreground">Content suggestions based on your interests</p>
                  </div>
                </div>
                <Switch 
                  checked={notifications.recommendations} 
                  onCheckedChange={(checked) => handleNotificationChange('recommendations', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </OnboardingLayout>
  );
};

// Calendar icons
const Calendar7 = ({ className }: { className?: string }) => (
  <div className={className}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <text x="12" y="19" textAnchor="middle" fontSize="8" fontWeight="bold" fill="currentColor">7</text>
    </svg>
  </div>
);

const Calendar30 = ({ className }: { className?: string }) => (
  <div className={className}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <text x="12" y="19" textAnchor="middle" fontSize="7" fontWeight="bold" fill="currentColor">30</text>
    </svg>
  </div>
);

const Mail = ({ className }: { className?: string }) => (
  <div className={className}>
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  </div>
);

const ThumbsUp = ({ className }: { className?: string }) => (
  <div className={className}>
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
  </div>
);

export default EmailPreferencesProps;
